/**
 * `ctx.graphics.exportMesh` — geometry out of the OS, as glTF/GLB or OBJ.
 *
 * Lifted out of Kitwright once it worked, which is the order that matters: a
 * glTF writer specified in the abstract gets the fiddly parts wrong quietly.
 * Byte alignment, accessor `min`/`max` (importers build bounding volumes from
 * them and silently cull without them), and the sRGB→linear colour path all
 * produce files that *open* and merely look wrong.
 *
 * The input is a plain `MeshPiece[]` — no THREE types, no scene graph, no app
 * state. That is deliberate: an exporter that called `updateMatrixWorld()` or
 * reached into a caller's store could not live here at all. Callers flatten
 * their scene once; the writers do the rest.
 *
 * Everything below `buildGLB`/`buildOBJ` is pure and unit-tested (`bbox.mjs`,
 * `color.mjs`), because the alternative — checking an exporter by opening its
 * output in Blender — is not something anyone repeats after a refactor.
 */

import { buildGLB } from "./gltf";
import { buildOBJ } from "./obj";
import type { MeshPiece, MeshExportOptions, MeshExportInfo } from "./types";

export type { MeshPiece, MeshGroup, MeshExportOptions, MeshExportInfo } from "./types";
export { DEFAULT_EXPORT_OPTIONS } from "./types";
export { buildGLB } from "./gltf";
export { buildOBJ } from "./obj";

export type MeshExportFormat = "glb" | "obj";

/** One output file. OBJ produces two (geometry + materials); GLB produces one. */
export type MeshExportFile = {
  /** Filename including extension, derived from `options.name`. */
  name: string;
  blob: Blob;
};

export type MeshExportResult = {
  format: MeshExportFormat;
  /**
   * Always an array, even for single-file formats — OBJ ships a `.obj` and a
   * `.mtl`, and a caller that assumed one file would drop the materials.
   */
  files: MeshExportFile[];
  info: MeshExportInfo;
};

export type MeshExportRequest = Partial<MeshExportOptions> & {
  format: MeshExportFormat;
};

/**
 * Write `pieces` out in the requested format.
 *
 * Throws on an empty piece list rather than producing a valid-but-empty file,
 * which reads downstream as "the export worked and my model vanished".
 */
export function exportMesh(
  pieces: readonly MeshPiece[],
  request: MeshExportRequest
): MeshExportResult {
  const { format, ...options } = request;
  const list = pieces as MeshPiece[];
  const name = options.name || "model";

  if (format === "obj") {
    const { obj, mtl, info } = buildOBJ(list, options);
    return {
      format,
      files: [
        { name: `${name}.obj`, blob: new Blob([obj], { type: "model/obj" }) },
        { name: `${name}.mtl`, blob: new Blob([mtl], { type: "model/mtl" }) },
      ],
      info,
    };
  }

  if (format === "glb") {
    const { buffer, info } = buildGLB(list, options);
    return {
      format,
      files: [
        {
          name: `${name}.glb`,
          blob: new Blob([buffer], { type: "model/gltf-binary" }),
        },
      ],
      info,
    };
  }

  throw new Error(`exportMesh: unsupported format ${String(format)}`);
}
